'use client';

import Image from '@/components/Image';
import WapOnly from '@/components/Responsive/WapOnly';
import WebOnly from '@/components/Responsive/WebOnly';
import { useEffect, useRef } from 'react';

export interface PgDescriptionAData {
  bg_color?: string; // 背景颜色
  type?: number; // 2=图片, 其他=视频
  image?: string; // 图片或视频URL
  title_config?: {
    title?: string; // 标题文字
    title_color?: string; // 标题颜色
  };
  description_config?: {
    description?: string; // 描述文字
    description_color?: string; // 描述颜色
  };
}

interface PgDescriptionAProps {
  id?: string;
  descriptionData: PgDescriptionAData;
  className?: string;
}

export default function PgDescriptionA({
  id,
  descriptionData,
  className = '',
}: PgDescriptionAProps) {
  const bannerVideoRef = useRef<HTMLVideoElement>(null);

  // 视频自动播放
  useEffect(() => {
    if (bannerVideoRef.current && descriptionData.type !== 2) {
      bannerVideoRef.current.play().catch(() => {
        // 处理自动播放失败的情况
      });
    }
  }, [descriptionData.type]);

  if (!descriptionData) {
    return null;
  }

  const containerStyle = {
    backgroundColor: descriptionData.bg_color || '#ffffff',
  };

  return (
    <div id={id} className={`description-part w-full ${className}`} style={containerStyle}>
      {/* PC端布局 */}
      <WebOnly>
        <div className="flex justify-between items-center w-full px-[40px] md:px-[70px] lg:px-[100px] xl:px-[130px] 2xl:px-[160px] py-[20px] md:py-[40px] xl:py-[60px] 2xl:py-[80px] gap-[60px] md:gap-[100px] lg:gap-[140px] xl:gap-[170px] 2xl:gap-[195px]">
          {/* 左侧文字内容 */}
          <div className="flex-1 w-full">
            {/* 标题 */}
            {descriptionData.title_config?.title && (
              <h2
                className="text-[20px] md:text-[28px] lg:text-[36px] xl:text-[40px] 2xl:text-[56px] leading-[24px] md:leading-[32px] lg:leading-[42px] xl:leading-[48px] 2xl:leading-[67px] font-bb mb-[16px] lg:mb-[20px] xl:mb-[22px] 2xl:mb-[24px]"
                style={{
                  color: descriptionData.title_config.title_color || '#282828',
                }}
              >
                {descriptionData.title_config.title}
              </h2>
            )}

            {/* 描述 */}
            {descriptionData.description_config?.description && (
              <h3
                className={`text-[14px] md:text-[16px] lg:text-[18px] xl:text-[20px] 2xl:text-[24px] leading-[18px] md:leading-[20px] lg:leading-[22px] xl:leading-[24px] 2xl:leading-[28px]`}
                style={{
                  color: descriptionData.description_config.description_color || '#282828',
                }}
                dangerouslySetInnerHTML={{
                  __html: descriptionData.description_config.description,
                }}
              />
            )}
          </div>

          {/* 右侧图片/视频 */}
          <div className="w-[300px] md:w-[400px] lg:w-[480px] xl:w-[500px] 2xl:w-[661px] flex-shrink-0">
            {descriptionData.type === 2 ? (
              // 图片
              <Image
                src={descriptionData.image || ''}
                alt="prabal gurung collection"
                width={661}
                height={400}
                className="w-full h-auto"
              />
            ) : (
              // 视频
              <div className="video-slide-out w-full h-full">
                <video
                  ref={bannerVideoRef}
                  className="video-slide block w-full h-full object-cover object-center"
                  loop
                  muted
                  playsInline
                  preload="auto"
                >
                  <source src={descriptionData.image} />
                </video>
              </div>
            )}
          </div>
        </div>
      </WebOnly>

      {/* 移动端布局 */}
      <WapOnly>
        <div className="w-full px-[14px] py-[28px]">
          <div className="img-part relative rounded-[10px] overflow-hidden">
            {descriptionData.type === 2 ? (
              // 图片
              <Image
                src={descriptionData.image || ''}
                alt="prabal gurung collection"
                width={375}
                height={250}
                className="w-full h-auto"
              />
            ) : (
              // 视频
              <div className="video-slide-out w-full h-full">
                <video
                  ref={bannerVideoRef}
                  className="video-slide block w-full h-full object-cover object-center"
                  loop
                  muted
                  playsInline
                  preload="auto"
                >
                  <source src={descriptionData.image} />
                </video>
              </div>
            )}

            {/* 文字覆盖层 */}
            <div className="word-part absolute top-0 left-0 w-full h-full flex flex-col justify-center items-center">
              {/* 标题 */}
              {descriptionData.title_config?.title && (
                <h2
                  className="text-[20px] leading-[24px] font-bb text-center mb-[4px]"
                  style={{
                    color: descriptionData.title_config.title_color || '#282828',
                  }}
                >
                  {descriptionData.title_config.title}
                </h2>
              )}

              {/* 描述 */}
              {descriptionData.description_config?.description && (
                <h3
                  className={`text-[12px] leading-[18px] text-center`}
                  style={{
                    color: descriptionData.description_config.description_color || '#282828',
                  }}
                  dangerouslySetInnerHTML={{
                    __html: descriptionData.description_config.description,
                  }}
                />
              )}
            </div>
          </div>
        </div>
      </WapOnly>
    </div>
  );
}
